/**
 * 包装好的http快捷api
 */
import Compressor from 'compressorjs';
import axios from 'axios';
import { _log, _error, _report } from './log';
import Domain from '../domain';
import { IMSDK, OBJTYPES, SDK } from '../sdk';
import API from './api';
let imsdk = IMSDK.create();

/**
 * 文件、图片上传选项
 */
export interface UploadOptions {
  /** 当图片size大于maxSize时是否开启图片压缩，仅图片上传时有效。默认开启：true */
  needCompress?: boolean;
  /** 图片压缩选项，详见 https://github.com/fengyuanchen/compressorjs#options */
  compressOption?: any;
  /** 图片、文件一次上传的最大数量，默认5。建议不要太多。 */
  maxNum?: number;
  /** 图片、文件的最大size，超过此值则停止上传，触发失败。 */
  maxSize?: number;
  /** 支持的文件/图片类型，图片默认支持 ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/gif']， 文件默认值 "*"（即不限制文件类型）  */
  acceptType?: Array<String> | String;
  /** 图片、文件预览回调函数。当图片，文件可以进行预览时会触发此回调。 */
  onPreview?(info: FileInfo): void;
  /** 上传进度，progress为当前上传进度，取值[0, 1]，1表示上传完成。可通过调用 controller.abort() 中止上传 */
  onProgress?(info: FileInfo, progress: number, controller: AbortController): void;
  // @internal 内部属性：语音时长
  voiceLength?: number;
}

/**
 * @internal
 * 文件、图片下载的参数
 */
export interface DownloadParams {
  /** 路径，一般为消息中的path字段 */
  path: string;
  /** 文件大小。长文本下载时传0 */
  size?: number;
  /** 文件名称，长文本下载时传空 */
  name?: string;
  /** 文件的存储位置，直接传入收到消息中的resStore即可 */
  resStore?: number;
}

/**
 * 文件预览/上传处理过程中的信息
 */
export interface FileInfo {
  /** 文件的唯一标识。用来标记文件，进行生成预览消息或者消息状态更新。如果预览前就发生错误（如超大，超数量，格式不符）则返回空。 */
  id?: number | string;
  /** 文件、图片、语音未上传之前，此值为固定值 'blob'，上传成功后为文件在server端的存储标识。  */
  uuid?: any;
  /** 文件、图片、语音未上传之前，此值为本地文件的blob url，上传成功后同 uuid */
  path?: any;
  /** 文件名称 */
  name?: string;
  /** 文件格式 */
  format?: string;
  /** 文件大小 */
  size?: number;
  /** 仅图片有效，图片高度 */
  width?: number;
  /** 仅图片有效，图片高度 */
  height?: number;
  /** 语音时长，仅语音有效 */
  length?: number;
}

/**
 * 文件上传结果
 */
export interface UploadResult {
  /** 错误码，0表示上传成功。非0表示发生错误 */
  code: number;
  /** 发生错误时的错误提示 */
  message?: string;
  /** 文件图片上传时的文件，仅执行预览操作后上传失败时返回，用于重试上传。 */
  file?: File;
  /** 文件信息。预览，预览后上传成功/失败会返回此信息 */
  info?: FileInfo;
}

/**
 * 图片上传默认配置
 */
const defaultOptForImg: UploadOptions = {
  // 开启自动压缩
  needCompress: true,
  // 压缩质量 0.8
  compressOption: {
    quality: 0.7,
  },

  maxNum: 5,
  maxSize: 4 * 1024 * 1024,
  acceptType: [
    'image/jpeg',
    'image/jpg',
    'image/bmp',
    'image/png',
    'image/gif',
  ],
};

/**
 * 文件上传默认配置
 */
const defaultOptForFile: UploadOptions = {
  maxNum: 5,
  maxSize: 100 * 1024 * 1024,
  acceptType: '*',
};

/**
 * 生成唯一uuid
 * @returns uuid
 * @example
 * ```ts
 * const message = {
 *  seq: SDKUtils.generateUUID(),
 *  type: MsgType.MSG_TIP,
 *  content: data.message,
 * }
 * ```
 */
export function generateUUID(): number {
  return Date.now() * 1000 + Math.floor(Math.random() * 1000);
}

// 获得文件大小描述文本
function computeSize(size: number): string {
  const M = 1024 * 1024;
  const K = 1024;
  if (size >= M) {
    return (size / M).toFixed(1) + 'MB';
  }
  if (size >= K) {
    return (size / K).toFixed(1) + 'KB';
  }
  return size + 'B';
}

// ------------IMAGE--------------
//检测文件大小
function checkSize(file: File, options: any = {}): any {
  if (file.size <= 5 || file.size > options.maxSize) {
    _error('file.size', file.size);
    return false;
  }

  return true;
}

// 检测文件类型
function checkType(file: File, options: any = {}): any {
  let isAccept = false;
  const acceptList = options.acceptType;
  const nameArray = file.name.split('.');
  // 如果未配置，或者配置了'*', 则不进行验证
  if (!acceptList || acceptList === '*') {
    return true;
  }
  for (let i = 0; i < acceptList.length; ++i) {
    if (
      file.type === acceptList[i] ||
      file.type.indexOf(acceptList[i]) !== -1 ||
      nameArray[nameArray.length - 1].indexOf(acceptList[i]) !== -1
    ) {
      isAccept = true;
      break;
    }
  }

  return isAccept;
}

// 压缩图片
function compressImg(image: File, options: any = {}): Promise<any> {
  return new Promise((resolve, reject) => {
    new Compressor(image, {
      quality: options.quality || 0.8,
      success(result) {
        _log('[compressImg success]');
        const blob = result;
        resolve(blob);
      },
      error(err) {
        _log('image compress error ---->>>>>', err);
        // 如果压缩失败，就直接返回原文件
        resolve(image);
      },
    });
  });
}

/** 处理文件上传结果 */
function parseUploadResult(file: File, info: any, uuid: any): UploadResult {
  if (uuid === false) {
    return {
      code: -10040,
      info,
      file,
      message: '上传失败: 网络异常',
    };
  } else {
    return {
      code: 0,
      info: { ...info, uuid, path: uuid },
    };
  }
}

// 加载图片进行预览
function previewImage(file: File): any {
  const info: any = {};
  info.id = generateUUID();
  info.name = file.name;
  info.size = file.size;
  info.format = file.type;
  info.uuid = 'blob';
  info.path = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = info.path;

    img.onload = () => {
      info.width = img.naturalWidth;
      info.height = img.naturalHeight;

      resolve(info);
    };
    img.onerror = (e) => {
      resolve(false);
    };
  });
}

/**
 * @internal
 * 上传操作 - 图片
 * @param file
 * @param info
 * @returns
 */
function doUploadImage(file: File, info: any, options: UploadOptions | null): Promise<any> {
  const imsdkMessage = imsdk.createImageUploadMessage!({
    size: info.size,
    name: info.name,
  });

  return new Promise((resolve) => {
    _report({
      module: 'c-imsdk-utils',
      method: 'doUploadImage',
      message: `[request][sign-url][${JSON.stringify(imsdkMessage)}]`,
    });
    const promise = imsdk.sendUploadOrDownloadMessage!(imsdkMessage);
    promise
      .then((res: any) => {
        const uploadUrl = `https://${res.host}${res.url}`;
        _report({
          module: 'c-imsdk-utils',
          method: 'doUploadImage',
          message: `[response][sign-url][${uploadUrl}]`,
        });
        // 上传控制器。用来中断上传进度
        const controller = new AbortController();
        axios
          .put(uploadUrl, file, {
            // content-type 写死。 因为文件上传签名验证会对这个值进行验证。
            // 而当漫游消息回显时，是无法获取到这个值 ，最终无法通过前面验证。因此先写死。
            headers: { 'Content-Type': 'application/octet-stream' },
            withCredentials: false,
            // 上传进度
            onUploadProgress: (progress) => {
              if (progress) {
                const progressNum = progress.loaded / progress.total;
                _log('[onUploadProgress]', progressNum);
                options && typeof (options.onProgress) === 'function' && options.onProgress(info, progressNum, controller);
              }
            },
            signal: controller.signal
          })
          .then((response) => {
            _report({
              module: 'c-imsdk-utils',
              method: 'doUploadImage',
              message: `[response][upload][${JSON.stringify(response)}]`,
            });
            if (response.status === 200) {
              resolve(res.uuid);
            } else {
              resolve(false);
            }
          })
          .catch(() => {
            _report({
              module: 'c-imsdk-utils',
              method: 'doUploadImage',
              message: `[error][upload][${uploadUrl}]`,
            });
            resolve(false);
          });
      })
      .catch(() => {
        _report({
          module: 'c-imsdk-utils',
          method: 'doUploadImage',
          message: `[error][sign-url][${JSON.stringify(imsdkMessage)}]`,
        });
        resolve(false);
      });
  });
}

/**
 * 上传图片
 * @param file 要上传的文件对象
 * @param reSendInfo 重试上传时文件的信息，
 * @param options 文件上传选项
 * @returns
 */
export async function uploadImage(
  file: File,
  reSendInfo: FileInfo | null = null,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const obj = '图片';
  // 如果有reSendInfo信息，则表示重发
  if (reSendInfo) {
    const uuid = await doUploadImage(file, reSendInfo, options);
    return parseUploadResult(file, reSendInfo, uuid);
  }

  const compressOption = {
    ...defaultOptForImg.compressOption,
    ...options.compressOption,
  };
  const opt = { ...defaultOptForImg, ...options, compressOption };

  // 检查文件类型
  const rsType = checkType(file, opt);
  if (rsType === false) {
    return {
      code: -10010,
      message: `不支持的${obj}格式${file.type ? ('：' + file.type ): '' }`,
    };
  }

  // 如果开启了压缩功能，并且图片size大于阈值，则进行压缩
  if (opt.needCompress && opt.maxSize && file.size > opt.maxSize) {
    // 压缩图片
    file = await compressImg(file, opt.compressOption);
  }

  // 压缩完之后在检测大小
  const rsSize = checkSize(file, opt);
  if (rsSize === false) {
    return {
      code: -10020,
      message: `发送${obj}大小超出限制(5B~${computeSize(opt.maxSize!)})`,
    };
  }

  const info = await previewImage(file);
  if (info === false) {
    return {
      code: -10030,
      message: `${obj}上传失败: 读取失败`,
    };
  }

  if (typeof opt.onPreview === 'function') {
    opt.onPreview(info);
  }

  // 上传操作
  const uuid = await doUploadImage(file, info, options);
  return parseUploadResult(file, info, uuid);
}

// -------------- FILE --------------
// 预览文件
function previewFile(file: File) {
  const info: any = {};
  info.id = generateUUID();
  info.name = file.name;
  info.size = file.size;
  info.format = file.type;
  info.uuid = 'blob';
  info.path = URL.createObjectURL(file);

  return info;
}

/**
 * @internal
 * 上传操作 - 文件
 * @param file
 * @param info
 * @param imsdk 多实例共存时，需要传入当前的imsdk实例。单实例不需要传入。
 * @returns
 */
function doUploadFile(file: File, info: any, options: UploadOptions | null): Promise<any> {
  const imsdkMessage = imsdk.createFileUploadMessage!(
    {
      size: info.size,
      name: info.name,
    },
    info.objType ? info.objType : false
  );

  return new Promise((resolve, reject) => {
    _report({
      module: 'c-imsdk-utils',
      method: 'doUploadFile',
      message: `[request][sign-url][${JSON.stringify(imsdkMessage)}]`,
    });
    const promise = imsdk.sendUploadOrDownloadMessage!(imsdkMessage);
    promise
      .then((res: any) => {
        const uploadUrl = `https://${res.host}${res.url}`;
        _report({
          module: 'c-imsdk-utils',
          method: 'doUploadFile',
          message: `[response][sign-url][${uploadUrl}]`,
        });
        // 上传控制器。用来中断上传进度
        const controller = new AbortController();
        axios
          .put(uploadUrl, file, {
            // content-type 写死。 因为文件上传签名验证会对这个值进行验证。
            // 而当漫游消息回显时，是无法获取到这个值 ，最终无法通过前面验证。因此先写死。
            headers: { 'Content-Type': 'application/octet-stream' },
            withCredentials: false,
            // 上传进度
            onUploadProgress: (progress) => {
              if (progress) {
                const progressNum = progress.loaded / progress.total;
                _log('[onUploadProgress]', progressNum);
                options && typeof (options.onProgress) === 'function' && options.onProgress(info, progressNum, controller);
              }
            },
            signal: controller.signal
          })
          .then((response) => {
            _report({
              module: 'c-imsdk-utils',
              method: 'doUploadFile',
              message: `[response][upload][${JSON.stringify(response)}]`,
            });
            if (response.status === 200) {
              resolve(res.uuid);
            } else {
              resolve(false);
            }
          })
          .catch(() => {
            _report({
              module: 'c-imsdk-utils',
              method: 'doUploadFile',
              message: `[error][upload][${uploadUrl}]`,
            });
            resolve(false);
          });
      })
      .catch(() => {
        _report({
          module: 'c-imsdk-utils',
          method: 'doUploadFile',
          message: `[error][sign-url][${JSON.stringify(imsdkMessage)}]`,
        });
        resolve(false);
      });
  });
}

/**
 * 上传文件
 * @param file
 * @param reSendInfo 重发图片的信息
 * @param options = {onPreview}
 * @returns
 */
export async function uploadFile(
  file: File,
  reSendInfo: FileInfo | any = null,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const obj = '文件';
  // 如果有reSendInfo信息，则表示重发
  if (reSendInfo) {
    const uuid = await doUploadFile(file, reSendInfo, options);
    return parseUploadResult(file, reSendInfo, uuid);
  }
  const opt = Object.assign(defaultOptForFile, options);

  // 检查文件类型
  const rsType = checkType(file, opt);
  if (rsType === false) {
    return {
      code: -10010,
      message: `不支持的${obj}格式${file.type ? ('：' + file.type ): '' }`,
    };
  }

  // 检查文件大小
  const rsSize = checkSize(file, opt);
  if (rsSize === false) {
    return {
      code: -10020,
      message: `发送${obj}大小超出限制(5B~${computeSize(opt.maxSize!)})`,
    };
  }

  // 预览文件
  const info = await previewFile(file);
  // 文件不存在预览失败的处理
  if (typeof opt.onPreview === 'function') {
    opt.onPreview(info);
  }

  // 上传操作
  const uuid = await doUploadFile(file, info, options);
  return parseUploadResult(file, info, uuid);
}

// -------------------------------- 语音 ----------------------

// 预览语音
function previewVoice(file: File, extInfo: any) {
  const info: any = {};
  info.id = generateUUID();
  info.name = file.name || 'voice.amr';
  info.size = file.size;
  info.format = file.type;
  info.uuid = 'blob';
  info.path = URL.createObjectURL(file);
  // 语音时长
  info.length = extInfo.voiceLength;

  return info;
}
/**
 * @internal
 * 执行语音上传
 * @param file
 * @param info
 * @param imsdk 多实例共存时，需要传入当前的imsdk实例。单实例不需要传入。
 * @returns
 */
function doUploadVoice(file: File, reSendInfo: any, options: UploadOptions | null): Promise<any> {
  return doUploadFile(
    file,
    Object.assign({}, reSendInfo, { objType: IMSDK.OBJTYPES.OBJ_VOICE }),
    options
  );
}

/**
 * 上传语音
 * @param file 语音文件
 * @param reSendInfo 重传时传递的数据
 * @param options 上传选项
 * @returns
 */
export async function uploadVoice(
  file: File,
  reSendInfo: FileInfo | null = null,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const obj = '语音';
  // 如果有reSendInfo信息，则表示重发
  if (reSendInfo) {
    const uuid = await doUploadVoice(file, reSendInfo, options);
    return parseUploadResult(file, reSendInfo, uuid);
  }

  const info = await previewVoice(file, { voiceLength: options.voiceLength });
  // 语音不存在预览失败的情况，无需处理
  if (typeof options.onPreview === 'function') {
    options.onPreview(info);
  }

  const uuid = await doUploadVoice(file, info, options);
  return parseUploadResult(file, info, uuid);
}

/**
 * 上传长文本。注意，长文本上传成功后仅返回 {code, info:{uuid}}
 * @param longText
 * @returns
 */
export function uploadLongText(longText: string): Promise<UploadResult> {
  const message = imsdk.createFileUploadMessage!(
    {},
    IMSDK.OBJTYPES.OBJ_LONGTEXT
  );

  return new Promise((resolve, reject) => {
    imsdk.sendUploadOrDownloadMessage!(message)
      .then((res: any) => {
        const uploadUrl = `https://${res.host}${res.url}`;
        axios
          .put(uploadUrl, longText, {
            // content-type 写死。 因为文件上传签名验证会对这个值进行验证。
            // 而当漫游消息回显时，是无法获取到这个值 ，最终无法通过前面验证。因此先写死。
            headers: { 'Content-Type': 'application/octet-stream' },
            withCredentials: false,
          })
          .then((response) => {
            if (response.status === 200) {
              resolve({
                code: 0,
                info: { uuid: res.uuid },
              });
            } else {
              resolve({
                code: response.status,
                message: '上传失败',
              });
            }
          })
          .catch((error) => {
            resolve({
              code: -1,
              message: '上传失败',
            });
          });
      })
      .catch((error) => {
        resolve({
          code: -1,
          message: '上传失败',
        });
      });
  });
}

// ---------------------------- 获得下载url ----------------------------------
/**
 * 获得文件下载的url
 * @param params 文件下载参数
 * @param type 文件的类型：普通文件、图片、语音还是文本（image|file|voice|text）
 * @returns 请求成功则返回 图片/文件的url，否则返回 空字符串
 */
export function getDownloadUrl(
  params: DownloadParams,
  type: OBJTYPES = 2
): Promise<string> {
  let imsdkMessage;
  if (type === IMSDK.OBJTYPES.OBJ_IMAGE) {
    imsdkMessage = imsdk.createImageDownloadMessage!(params);
  } else if (
    type === IMSDK.OBJTYPES.OBJ_FILE ||
    type === IMSDK.OBJTYPES.OBJ_VOICE
  ) {
    imsdkMessage = imsdk.createFileDownloadMessage!(params);
  } else if (type === IMSDK.OBJTYPES.OBJ_LONGTEXT) {
    imsdkMessage = imsdk.createFileDownloadMessage!(
      params,
      IMSDK.OBJTYPES.OBJ_LONGTEXT
    );
  }

  if (!imsdkMessage) {
    _error('[getDownloadUrl] type error', type);
    return Promise.resolve('');
  }

  const promise = imsdk.sendUploadOrDownloadMessage!(imsdkMessage);
  return new Promise((resolve) => {
    promise
      .then((res: any) => {
        const formatPath = `https://${res.host}${res.url}`;
        resolve(formatPath);
      })
      .catch(() => {
        resolve('');
      });
  });
}

/**
 * SDK通路文件下载参数
 */
export interface DownloadOptions {
  /** 路径，一般为消息中的path字段 */
  path: string;
  /** @internal 部分逻辑需要传入桶数据，默认为0 */
  cosBucket: number;
  /** 是否压缩的内容，直接将消息中返回的compressed值传入 */
  compressed: number | boolean;
}

/**
 * 下载并解压解密长文本数据（包括xml数据）。
 * webim的长文本在发送的过程中，会被单独上传压缩处理，因此需要调用专门的接口去下载解压和解析。
 * @param params 下载参数
 * @param type 下载数据的类型。 可选值： xml 或 txt， 默认 txt;
 * @example
 * ```ts
 * if (this.data.compressed) {
 *    // 如果是SDK通路的被压缩的长文本
 *    try {
 *      this.longMessage = await SDKUtils.downloadAndParseLongText(this.data);
 *    } catch (ex) {
 *      console.error(ex);
 *      this.longMessage = '[获取消息内容异常]';
 *    }
 *  } else {
 *    const resPath = await SDKUtils.getDownloadUrl(this.data, IMSDK.OBJTYPES.OBJ_LONGTEXT);
 *    if (resPath) {
 *      axios.get(resPath).then((response) => {
 *        if (response.status === 200) {
 *          this.longMessage = response.data;
 *        } else {
 *          this.longMessage = '[获取消息内容异常]';
 *        }
 *      })
 *        .catch(() => {
 *          this.longMessage = '[获取消息内容异常]';
 *        });
 *    } else {
 *      this.longMessage = '[获取消息内容异常]';
 *    }
 *  }
 * ```
 */
export function downloadAndParseLongText(
  params: DownloadOptions,
  type: string = 'txt'
) {
  const data = imsdk.createCompressedLongTextDownloadMessage!(params, type);

  return new Promise((resolve, reject) => {
    // 获得内置域名
    const IM_DOMAIN = Domain.getSDKDomain();

    axios
      .post(IM_DOMAIN.api + API.DOWNLOAD_COMPRESSED_COTENT, data)
      .then((res) => {
        if (res.status === 200) {
          const { code, data } = res.data;
          if (!code) {
            // XML, 长文本 分别会从不同的路径获得数据
            const rsInfo = data.long_msg_info || data.struct_msg_info;
            resolve(rsInfo.str_content);
          }
        }

        reject(false);
      })
      .catch((err) => {
        _error('[downloadAndParseLongText]', err);
        reject(false);
      });
  });
}


/**
 * @internal
 * C侧主动退出排队
 * @param params 
 */
 export function exitQueue(params: any) {
  // 获得内置域名
  const IM_DOMAIN = Domain.getSDKDomain();
  
  const data = imsdk.createExitQueueMessage!(params);

  return axios.post(IM_DOMAIN.api + API.EXIT_QUEUE, data);
}

/**
 * 多im实例共存时，调用 SDKUtils.uploadXXX 等方法均需要先设置当前实例。单实例模式会自动获取全局实例。
 * @param newimsdk imsdk实例
 * @example
 * ```ts
 * const imsdk = SDKUtils.newQDIM(...);
 * SDKUtils.setIMInstance(imsdk);
 * ```
 */
export function setIMInstance(newimsdk: SDK) {
  imsdk = newimsdk;
}
